import React, { useState, useRef, useEffect } from 'react';
import './Trek.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Trek Component ---

export default function Trek() {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch milestones from API
  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        setLoading(true);
        const result = await apiService.fetchAboutStory();
        
        console.log('📜 Trek milestones response:', result);
        
        if (result?.status === 'success' && result?.data?.length > 0) {
          // Sort by display_order
          const sorted = result.data.sort((a, b) => 
            parseInt(a.display_order || 0) - parseInt(b.display_order || 0)
          );
          setMilestones(sorted);
          console.log('✅ Milestones set:', sorted.length);
        } else {
          console.warn('⚠️ No milestones found');
          setError('No milestones data available');
        }
      } catch (err) {
        console.error('❌ Error fetching milestones:', err);
        setError(err.message || 'Failed to load milestones');
      } finally {
        setLoading(false);
      }
    };

    fetchMilestones();
  }, []);

  // Get unique categories from data
  const categories = ['all', ...new Set(milestones.map(m => m.category || 'General'))];

  const filteredMilestones = selectedCategory === 'all' 
    ? milestones 
    : milestones.filter(m => (m.category || 'General') === selectedCategory);

  // Stats from API data
  const getStats = () => {
    if (milestones.length === 0) {
      return [
        { value: '500+', label: 'Students' },
        { value: '36+', label: 'Years of Service' },
        { value: '1000+', label: 'Braille Books' },
        { value: '300+', label: 'Awards' },
        { value: '100%', label: 'Free Education' }
      ];
    }
    
    const years = milestones.map(m => parseInt(m.year)).filter(y => !isNaN(y));
    const uniqueYears = [...new Set(years)];
    const totalYears = uniqueYears.length > 0 ? Math.max(...uniqueYears) - Math.min(...uniqueYears) + 1 : 36;
    
    return [
      { value: `${milestones.length}+`, label: 'Milestones' },
      { value: `${totalYears}+`, label: 'Years of Service' },
      { value: `${uniqueYears.length}`, label: 'Active Years' },
      { value: '100%', label: 'Free Education' }
    ];
  };

  const stats = getStats();

  // Get icon based on title/description
  const getIcon = (title, description) => {
    const text = (title + ' ' + description).toLowerCase();
    if (text.includes('award') || text.includes('president') || text.includes('recognition')) return '🏆';
    if (text.includes('school') || text.includes('hostel') || text.includes('building')) return '🏫';
    if (text.includes('computer') || text.includes('software') || text.includes('technology')) return '💻';
    if (text.includes('braille') || text.includes('book') || text.includes('print')) return '📖';
    if (text.includes('cricket') || text.includes('chess') || text.includes('sport')) return '🏏';
    if (text.includes('election') || text.includes('evm') || text.includes('vote')) return '🗳️';
    if (text.includes('student') || text.includes('strength') || text.includes('growth')) return '📈';
    if (text.includes('graduate') || text.includes('exam') || text.includes('college')) return '🎓';
    if (text.includes('innovation') || text.includes('research') || text.includes('science')) return '🔬';
    if (text.includes('international') || text.includes('country') || text.includes('world')) return '🌍';
    if (text.includes('record') || text.includes('limca')) return '📖';
    if (text.includes('health') || text.includes('medical') || text.includes('doctor')) return '🩺';
    if (text.includes('foundation') || text.includes('trust') || text.includes('start')) return '🌱';
    return '⭐';
  };

  // Get category based on title/description
  const getCategory = (title, description) => {
    const text = (title + ' ' + description).toLowerCase();
    if (text.includes('award') || text.includes('president') || text.includes('recognition') || text.includes('honour')) return 'Recognition';
    if (text.includes('school') || text.includes('hostel') || text.includes('building') || text.includes('infrastructure')) return 'Infrastructure';
    if (text.includes('computer') || text.includes('software') || text.includes('technology') || text.includes('braille')) return 'Technology';
    if (text.includes('student') || text.includes('strength') || text.includes('growth') || text.includes('international')) return 'Growth';
    if (text.includes('cricket') || text.includes('chess') || text.includes('sport')) return 'Sports';
    if (text.includes('election') || text.includes('evm') || text.includes('vote')) return 'Impact';
    if (text.includes('graduate') || text.includes('exam') || text.includes('college') || text.includes('education')) return 'Education';
    if (text.includes('innovation') || text.includes('research') || text.includes('science')) return 'Innovation';
    if (text.includes('record') || text.includes('limca')) return 'Recognition';
    if (text.includes('foundation') || text.includes('trust') || text.includes('start')) return 'Foundation';
    return 'General';
  };

  // Prepare milestones with icons and categories
  const preparedMilestones = milestones.map(m => ({
    ...m,
    icon: getIcon(m.title_en || m.title, m.description_en || m.description),
    category: m.category || getCategory(m.title_en || m.title, m.description_en || m.description)
  }));

  const displayMilestones = preparedMilestones.length > 0 ? preparedMilestones : [];

  return (
    <>
      <Header />
      <div className="trek-page devnar">
        <a className="skip-link" href="#main-trek">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-trek">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-trek">
            <Reveal>
              <span className="hero-badge">Since 1991</span>
              <h1>Devnar — 30 Years Trek</h1>
              <p className="hero-subtitle">
                A journey of three decades transforming the lives of visually challenged children 
                through education, innovation, and unwavering dedication.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-trek" className="wrap page-content-trek">

          {/* --- Stats --- */}
          <section className="trek-stats">
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <Reveal key={stat.label} className="stat-card" delay={index * 60}>
                  <div className="stat-number">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Filter Tabs --- */}
          <section className="trek-filter">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Journey</span>
              <h2>30 Years of Excellence</h2>
              <div className="rule" />
              <p className="section-subtitle">
                From 4 students in 1991 to over 500 students today — a journey of milestones, 
                recognition, and transformative impact.
              </p>
            </Reveal>
            
            {/* {displayMilestones.length > 0 && (
              <div className="filter-tabs">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category === 'all' ? 'All Milestones' : category}
                  </button>
                ))}
              </div>
            )} */}
          </section>

          {/* --- Timeline --- */}
          <section className="trek-timeline">
            {loading ? (
              <div className="loading-state">
                <p>Loading milestones...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>Unable to load milestones. Please try again later.</p>
              </div>
            ) : displayMilestones.length === 0 ? (
              <div className="empty-state">
                <p>No milestones available.</p>
              </div>
            ) : (
              <div className="timeline-container">
                {filteredMilestones.map((milestone, index) => (
                  <Reveal 
                    key={milestone.id || `${milestone.year}-${index}`} 
                    className="timeline-item-wrapper" 
                    delay={(index % 10) * 50}
                  >
                    <div className="timeline-item">
                      <div className="timeline-year-badge">
                        <span className="year">{milestone.year}</span>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-icon">{milestone.icon}</span>
                          <h3>{milestone.title_en || milestone.title}</h3>
                        </div>
                        <p className="timeline-description">{milestone.description_en || milestone.description}</p>
                        <span className="timeline-category">{milestone.category}</span>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </section>

          {/* --- Key Achievements Summary --- */}
          {displayMilestones.length > 0 && (
            <section className="key-achievements">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Highlights</span>
                <h2>Key Achievements</h2>
                <div className="rule" />
              </Reveal>
              <div className="achievements-grid">
                <Reveal className="achievement-card" delay={0}>
                  <span className="achievement-icon">🏆</span>
                  <h3>Presidential Awards</h3>
                  <p>Honoured by multiple Presidents of India including Dr. A.P.J. Abdul Kalam</p>
                </Reveal>
                <Reveal className="achievement-card" delay={80}>
                  <span className="achievement-icon">⭐</span>
                  <h3>Padma Shri Award</h3>
                  <p>Highest civilian award received in 2009</p>
                </Reveal>
                <Reveal className="achievement-card" delay={160}>
                  <span className="achievement-icon">📖</span>
                  <h3>Braille Innovation</h3>
                  <p>Pioneering Braille printing and technology for visually challenged</p>
                </Reveal>
                <Reveal className="achievement-card" delay={240}>
                  <span className="achievement-icon">🎓</span>
                  <h3>Education Firsts</h3>
                  <p>First Intermediate College, Engineering graduates, CA graduate for visually challenged</p>
                </Reveal>
                <Reveal className="achievement-card" delay={320}>
                  <span className="achievement-icon">🗳️</span>
                  <h3>EVM Modification</h3>
                  <p>Enabled independent voting for visually challenged across India</p>
                </Reveal>
                <Reveal className="achievement-card" delay={400}>
                  <span className="achievement-icon">🌍</span>
                  <h3>International Impact</h3>
                  <p>Students from multiple countries and international recognition</p>
                </Reveal>
              </div>
            </section>
          )}

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-trek">
            <div className="cta-content">
              <span className="cta-eyebrow">Join Our Journey</span>
              <h2>Be Part of the Next 30 Years</h2>
              <p>
                Help us continue this legacy of excellence and empower more visually challenged 
                children to achieve their dreams.
              </p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="/donate">Support Our Mission</a>
                <a className="btn btn-outline btn-outline-light" href="/volunteer">Become a Volunteer</a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}